import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import {
    Printer,
    Download,
    ArrowLeft,
    Lock,
    ShieldCheck,
    Pencil,
    Trophy,
    Sparkles,
    CheckCircle2,
    Circle,
} from "lucide-react";
import { BRANCH_META } from "@/lib/branches";
import { useAuth } from "@/context/AuthContext";

/**
 * Public traditional-format resume — Harvard MCS layout.
 * Route: /u/:username/resume
 *
 * Structure (top to bottom):
 *   1. Contact header — name centered, address · email · phone underneath
 *   2. Education                (user-editable — Harvard convention)
 *   3. 🔒 Project Atlas Verified Ledger  (system-populated, un-fakeable)
 *   4. Experience / Work History (user-editable)
 *   5. Leadership & Activities   (user-editable)
 *   6. Skills & Interests        (user-editable)
 *
 * Every user-editable section is fed from PUT /me/resume-fields via the
 * ResumeEditor page. The Verified Ledger is the visual centerpiece — it
 * is subtly tinted and stamped so a hiring manager knows exactly which
 * claims can and can't be gamed.
 */
export default function Resume() {
    const { username } = useParams();
    const { user: me } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/public/resume/${username}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Resume not found"));
    }, [username]);

    if (error) {
        return (
            <div className="min-h-screen w-full flex items-center justify-center px-6">
                <div className="crystal rounded-3xl p-8 max-w-md text-center">
                    <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900">
                        {error}
                    </h1>
                    <Link to="/" className="arcade-btn mt-6 inline-flex" data-testid="resume-error-home">
                        <ArrowLeft className="w-4 h-4" /> Back to Atlas
                    </Link>
                </div>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="min-h-screen w-full flex items-center justify-center text-slate-500 font-display">
                Loading resume…
            </div>
        );
    }

    const u = data.user;
    const isOwner = !!me && me.username === u.username;
    const contactBits = [data.contact.location, data.contact.email, data.contact.phone]
        .map((s) => (s || "").trim())
        .filter(Boolean);

    return (
        <div className="min-h-screen w-full bg-slate-100 py-6 sm:py-12 print:bg-white print:py-0">
            {/* Toolbar — hidden when printing */}
            <div
                className="max-w-4xl mx-auto px-4 sm:px-8 mb-4 flex flex-wrap items-center gap-2 print:hidden"
                data-testid="resume-toolbar"
            >
                <Link
                    to={`/u/${username}`}
                    className="arcade-btn"
                    data-testid="resume-back-to-profile"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to profile
                </Link>
                <div className="ml-auto flex gap-2 flex-wrap">
                    {isOwner && (
                        <Link
                            to="/settings/resume"
                            className="arcade-btn"
                            data-testid="resume-edit"
                        >
                            <Pencil className="w-4 h-4" /> Edit
                        </Link>
                    )}
                    <button
                        onClick={() => window.print()}
                        className="arcade-btn"
                        data-variant="primary"
                        data-testid="resume-print"
                    >
                        <Printer className="w-4 h-4" /> Print / Save as PDF
                    </button>
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/profile/resume.pdf`}
                        className="arcade-btn"
                        data-testid="resume-download-ledger-pdf"
                        title="Download the alternative Living-Ledger PDF"
                    >
                        <Download className="w-4 h-4" /> Ledger PDF
                    </a>
                </div>
            </div>

            {/* The document itself — serif type, centered headers */}
            <article
                className="max-w-4xl mx-auto bg-white shadow-2xl rounded-lg print:shadow-none print:rounded-none px-8 sm:px-16 py-10 sm:py-14 text-slate-900 print:max-w-full print:px-12 print:py-10"
                data-testid="resume-document"
                style={{ fontFamily: '"EB Garamond", "Georgia", "Times New Roman", serif', fontSize: "12pt", lineHeight: 1.35 }}
            >
                {/* Header */}
                <header className="text-center pb-2" data-testid="resume-header">
                    <h1
                        className="font-bold text-4xl tracking-tight text-slate-900 leading-none"
                        data-testid="resume-name"
                    >
                        {u.name}
                    </h1>
                    {contactBits.length > 0 && (
                        <p className="mt-2 text-sm text-slate-700" data-testid="resume-contact">
                            {contactBits.join(" · ")}
                        </p>
                    )}
                    {data.contact.website && (
                        <p className="text-sm text-slate-700">
                            <a
                                href={data.contact.website}
                                target="_blank"
                                rel="noreferrer"
                                className="text-slate-700 hover:underline"
                            >
                                {data.contact.website.replace(/^https?:\/\//, "")}
                            </a>
                        </p>
                    )}
                </header>

                {/* EDUCATION */}
                <Section title="Education" testid="resume-education">
                    {(data.education || []).length === 0 ? (
                        <EmptyRow
                            isOwner={isOwner}
                            label="Add your degree, coursework, and honors so hiring managers see your foundation."
                            testid="resume-education-empty"
                        />
                    ) : (
                        <div className="space-y-3">
                            {(data.education || []).map((e, i) => (
                                <EducationRow key={i} row={e} index={i} />
                            ))}
                        </div>
                    )}
                </Section>

                {/* 🔒 VERIFIED LEDGER — the un-fakeable middle */}
                <Section
                    title="Project Atlas Verified Ledger"
                    testid="resume-ledger"
                    stamp
                    subtitle={
                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                            <Lock className="w-3 h-3" /> Locked · verified live on project-atlas.link
                        </span>
                    }
                >
                    <VerifiedLedger data={data} username={u.username} />
                </Section>

                {/* WORK EXPERIENCE */}
                <Section title="Experience" testid="resume-work">
                    {(data.work_history || []).length === 0 ? (
                        <EmptyRow
                            isOwner={isOwner}
                            label="Add roles that fall outside Atlas — the ledger already tells the rest."
                            testid="resume-work-empty"
                        />
                    ) : (
                        <div className="space-y-3">
                            {(data.work_history || []).map((w, i) => (
                                <BulletRow key={i} row={w} orgKey="organization" roleKey="position" testid={`resume-work-${i}`} />
                            ))}
                        </div>
                    )}
                </Section>

                {/* LEADERSHIP */}
                {(data.leadership || []).length > 0 && (
                    <Section title="Leadership & Activities" testid="resume-leadership">
                        <div className="space-y-3">
                            {(data.leadership || []).map((l, i) => (
                                <BulletRow key={i} row={l} orgKey="organization" roleKey="role" testid={`resume-lead-${i}`} />
                            ))}
                        </div>
                    </Section>
                )}

                {/* SKILLS & INTERESTS */}
                {hasSkills(data.skills_interests) && (
                    <Section title="Skills & Interests" testid="resume-skills-block">
                        <SkillsList si={data.skills_interests} />
                    </Section>
                )}

                {/* Footer signature */}
                <footer
                    className="mt-8 pt-3 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-500"
                    style={{ fontFamily: '"Inter", sans-serif' }}
                >
                    <span>
                        Every ledger line above is verifiable at project-atlas.link{data.contact.profile_url}
                    </span>
                    <span className="font-mono">
                        generated {new Date(data.generated_at).toISOString().slice(0, 10)}
                    </span>
                </footer>
            </article>

            {/* Print stylesheet */}
            <style>{`
                @media print {
                    body { background: white !important; }
                    @page { margin: 0.5in; }
                }
            `}</style>
        </div>
    );
}

/* ------------------------------- Section shell ---------------------------- */

function Section({ title, testid, subtitle, stamp, children }) {
    return (
        <section className="mt-6" data-testid={testid}>
            <div className="text-center pb-1 mb-3 border-b border-slate-900 relative">
                <h2 className="font-bold text-[13pt] tracking-wider uppercase text-slate-900 inline-flex items-center gap-2">
                    {stamp && <ShieldCheck className="w-4 h-4 text-slate-900" />}
                    {title}
                </h2>
                {subtitle && <div className="mt-0.5">{subtitle}</div>}
            </div>
            {children}
        </section>
    );
}

function EmptyRow({ label, isOwner, testid }) {
    return (
        <div className="text-sm text-slate-500 italic px-2 print:hidden" data-testid={testid}>
            {label}
            {isOwner && (
                <>
                    {" "}
                    <Link
                        to="/settings/resume"
                        className="not-italic text-humanities font-bold hover:underline inline-flex items-center gap-1"
                    >
                        <Pencil className="w-3 h-3" /> Add
                    </Link>
                </>
            )}
        </div>
    );
}

/* -------------------------------- Education ------------------------------- */

function EducationRow({ row, index }) {
    const line1Right = row.dates;
    return (
        <div className="text-[11.5pt]" data-testid={`resume-edu-row-${index}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <div className="font-bold">
                    {row.institution}
                    {row.location && <span className="font-normal text-slate-700"> — {row.location}</span>}
                </div>
                {line1Right && <div className="text-slate-700 whitespace-nowrap">{line1Right}</div>}
            </div>
            <div>
                {[row.degree, row.concentration].filter(Boolean).join(", ")}
                {row.gpa && <span className="text-slate-700"> · GPA {row.gpa}</span>}
            </div>
            {row.thesis && <div className="italic text-slate-800">Thesis: {row.thesis}</div>}
            {row.coursework && (
                <div className="text-slate-800">
                    <span className="italic">Relevant Coursework: </span>
                    {row.coursework}
                </div>
            )}
            {row.honors && (
                <div className="text-slate-800">
                    <span className="italic">Honors: </span>
                    {row.honors}
                </div>
            )}
        </div>
    );
}

/* --------------------- Work / Leadership bullet rows ---------------------- */

function BulletRow({ row, orgKey, roleKey, testid }) {
    return (
        <div className="text-[11.5pt] break-inside-avoid" data-testid={testid}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <div>
                    <span className="font-bold">{row[orgKey]}</span>
                    {row[roleKey] && <span className="text-slate-800">, {row[roleKey]}</span>}
                    {row.location && <span className="text-slate-700"> — {row.location}</span>}
                </div>
                {row.dates && <div className="text-slate-700 whitespace-nowrap">{row.dates}</div>}
            </div>
            {row.bullets && row.bullets.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                    {row.bullets.map((b, i) => (
                        <li key={i} className="flex gap-2">
                            <span className="text-slate-400 flex-shrink-0">•</span>
                            <span className="text-slate-900">{b}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/* --------------------- Verified Ledger — the centerpiece ------------------ */

function VerifiedLedger({ data, username }) {
    const u = data.user;
    const branchXpTotal = Object.values(u.branch_xp || {}).reduce((a, b) => a + b, 0) || 1;
    const masters = data.stats?.master_branches || [];
    const cross = data.cross_discipline;
    const experience = data.experience || [];
    const certifications = data.certifications || [];
    const careerPaths = data.career_paths || [];
    const skills = data.skills || [];

    const hasAny =
        masters.length > 0 ||
        certifications.length > 0 ||
        experience.length > 0 ||
        careerPaths.length > 0 ||
        !!cross ||
        skills.length > 0;

    if (!hasAny) {
        return (
            <div className="text-sm text-slate-500 italic px-2" data-testid="resume-ledger-empty">
                No verified activity yet. Contribute on Atlas and every commit lands here — permanently.
            </div>
        );
    }

    return (
        <div
            className="rounded-lg bg-amber-50/40 border border-amber-200/50 p-4 print:bg-white print:border-slate-300"
            data-testid="resume-ledger-block"
        >
            {/* Master-of chips + level */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[10pt] font-bold text-slate-700 mr-1">
                    Level {data.stats?.level || 1} · {data.stats?.xp || 0} XP · {data.stats?.total_contributions || 0} verified contributions
                </span>
                {masters.map((slug) => {
                    const meta = BRANCH_META[slug];
                    return (
                        <span
                            key={slug}
                            className="text-[9pt] font-bold px-2 py-0.5 rounded"
                            style={{
                                background: meta?.soft || "#EEF2FF",
                                color: meta?.text || "#3730A3",
                                fontFamily: '"Inter", sans-serif',
                            }}
                            data-testid={`resume-ledger-master-${slug}`}
                        >
                            Master of {meta?.name || slug}
                        </span>
                    );
                })}
            </div>

            {/* Branch XP distribution */}
            <div className="mb-4" data-testid="resume-ledger-branches">
                <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
                    {["sciences", "arts", "trades", "humanities", "business"].map((slug) => {
                        const v = (u.branch_xp || {})[slug] || 0;
                        const pct = (v / branchXpTotal) * 100;
                        if (pct === 0) return null;
                        return (
                            <div
                                key={slug}
                                style={{ width: `${pct}%`, background: BRANCH_META[slug].color }}
                                title={`${BRANCH_META[slug].name} · ${v} XP`}
                            />
                        );
                    })}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[9pt] text-slate-600">
                    {["sciences", "arts", "trades", "humanities", "business"].map((slug) => {
                        const v = (u.branch_xp || {})[slug] || 0;
                        if (v === 0) return null;
                        return (
                            <span key={slug} className="inline-flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: BRANCH_META[slug].color }} />
                                {BRANCH_META[slug].name} {v} XP
                            </span>
                        );
                    })}
                </div>
            </div>

            {/* Cross-discipline */}
            {cross && <LedgerCross cross={cross} />}

            {/* Verified contributions grouped by project */}
            {experience.length > 0 && (
                <div className="mt-2" data-testid="resume-ledger-experience">
                    <div className="text-[10pt] font-bold uppercase tracking-widest text-slate-600 mb-1">
                        Verified contributions
                    </div>
                    <div className="space-y-2">
                        {experience.map((exp, i) => (
                            <LedgerExperience key={i} exp={exp} index={i} />
                        ))}
                    </div>
                </div>
            )}

            {/* Merit badges */}
            {certifications.length > 0 && (
                <div className="mt-3" data-testid="resume-ledger-badges">
                    <div className="text-[10pt] font-bold uppercase tracking-widest text-slate-600 mb-1">
                        Merit badges
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {certifications.map((c, i) => {
                            const meta = BRANCH_META[c.branch] || BRANCH_META.arts;
                            return (
                                <span
                                    key={i}
                                    className="text-[9.5pt] px-2 py-0.5 rounded inline-flex items-center gap-1.5"
                                    style={{ background: meta.soft, color: meta.text, fontFamily: '"Inter", sans-serif' }}
                                    data-testid={`resume-ledger-badge-${i}`}
                                >
                                    <Trophy className="w-3 h-3" />
                                    {c.name}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Career paths */}
            {careerPaths.length > 0 && (
                <div className="mt-3" data-testid="resume-ledger-paths">
                    <div className="text-[10pt] font-bold uppercase tracking-widest text-slate-600 mb-1">
                        Career paths
                    </div>
                    <div className="space-y-1">
                        {careerPaths.map((cp, i) => {
                            const completed = !!cp.completed_at;
                            const dateStr = (cp.completed_at || cp.started_at || "").slice(0, 10);
                            return (
                                <div key={i} className="flex flex-wrap items-baseline justify-between gap-x-3 text-[11pt]" data-testid={`resume-ledger-path-${i}`}>
                                    <div>
                                        <span className="font-bold">{cp.org_name}</span>
                                        <span className="text-slate-800"> — {cp.role_title}</span>
                                        {!completed && (
                                            <span className="text-slate-500 text-xs"> ({cp.progress_pct}% complete)</span>
                                        )}
                                    </div>
                                    <div className="text-slate-700 whitespace-nowrap text-xs">
                                        {completed ? `✓ Completed ${dateStr}` : `Started ${dateStr}`}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Discipline skills summary */}
            {skills.length > 0 && (
                <div className="mt-3 text-[10.5pt]" data-testid="resume-ledger-skills">
                    <span className="font-bold uppercase tracking-widest text-[10pt] text-slate-600">Disciplines: </span>
                    {skills.map((s, i) => (
                        <span key={s.discipline}>
                            {i > 0 && "; "}
                            <span className="text-slate-800">
                                {s.discipline.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                            </span>
                            <span className="text-slate-500"> ({s.contributions})</span>
                        </span>
                    ))}
                </div>
            )}

            {/* Verifier line */}
            <div
                className="mt-4 pt-2 border-t border-amber-200/70 flex items-center justify-between text-[9pt] text-slate-500"
                style={{ fontFamily: '"Inter", sans-serif' }}
            >
                <span className="inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Verified on Project Atlas
                </span>
                <span className="font-mono">project-atlas.link/u/{username}</span>
            </div>
        </div>
    );
}

function LedgerExperience({ exp, index }) {
    const meta = BRANCH_META[exp.discipline] || null;
    const start = (exp.started_at || "").slice(0, 10);
    const end = (exp.ended_at || "").slice(0, 10);
    const dateStr = start && end && start !== end ? `${start} → ${end}` : start || end || "";
    return (
        <div className="text-[11pt] break-inside-avoid" data-testid={`resume-ledger-exp-${index}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <div className="min-w-0">
                    <span className="font-bold">{exp.title}</span>
                    <span className="text-slate-800">, {exp.role}</span>
                    <span className="text-slate-700"> — {exp.org}</span>
                    {meta && (
                        <span className="text-slate-600 text-[10pt]"> · {meta.name}</span>
                    )}
                </div>
                {dateStr && <div className="text-slate-700 whitespace-nowrap text-xs">{dateStr}</div>}
            </div>
            {exp.bullets && exp.bullets.length > 0 && (
                <ul className="mt-0.5 space-y-0.5 pl-4">
                    {exp.bullets.map((b, i) => (
                        <li key={i} className="flex gap-2 text-[11pt]">
                            <span className="text-slate-400 flex-shrink-0">•</span>
                            <span className="text-slate-900">{b.message}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function LedgerCross({ cross }) {
    const [a, b] = cross.disciplines || [];
    if (!a || !b) return null;
    const metaA = BRANCH_META[a.branch] || BRANCH_META.arts;
    const metaB = BRANCH_META[b.branch] || BRANCH_META.arts;
    const completed = !!cross.completed_at;
    return (
        <div
            className="mb-3 p-3 rounded border"
            style={{ borderColor: metaA.color, background: `${metaA.soft}55` }}
            data-testid="resume-ledger-cross"
        >
            <div className="text-[9pt] uppercase tracking-widest font-bold inline-flex items-center gap-1" style={{ color: metaA.text, fontFamily: '"Inter", sans-serif' }}>
                <Sparkles className="w-3 h-3" /> Cross-discipline · {a.name} × {b.name}
            </div>
            <div className="text-[11.5pt] font-bold mt-0.5">
                {cross.org_name}
                <span className="font-normal"> — {cross.role_title}</span>
            </div>
            {typeof cross.progress_pct === "number" && cross.total_steps > 0 && (
                <div className="mt-1 text-[10pt] text-slate-700">
                    {cross.done_steps}/{cross.total_steps} steps · {cross.progress_pct}%
                    {completed && <span className="ml-2 text-[9pt] font-bold" style={{ color: metaA.text }}>✓ Completed</span>}
                </div>
            )}
        </div>
    );
}

/* ------------------------------ Skills block ------------------------------ */

function hasSkills(si) {
    if (!si || typeof si !== "object") return false;
    return !!(si.technical || si.languages || si.laboratory || si.interests);
}

function SkillsList({ si }) {
    const rows = [
        { label: "Technical", value: si.technical },
        { label: "Languages", value: si.languages },
        { label: "Laboratory", value: si.laboratory },
        { label: "Interests", value: si.interests },
    ].filter((r) => r.value);
    return (
        <div className="space-y-0.5 text-[11.5pt]" data-testid="resume-skills-list">
            {rows.map((r) => (
                <div key={r.label}>
                    <span className="font-bold">{r.label}: </span>
                    <span className="text-slate-800">{r.value}</span>
                </div>
            ))}
        </div>
    );
}
